/**
 * CELESTIAL BODIES — the sun and moon discs (glow, halo, disc, crescent carve).
 */

import { normalizeTimeOfDay, lerp, rgb, lerpRgb } from "./sky-utils.js";

function overlayRadialRgb(dst, t, c0, a0, c1, a1, c2, a2) {
  var c;
  var a;
  var k;
  if (t < 0.35) {
    k = t / 0.35;
    c = lerpRgb(c0, c1, k);
    a = lerp(a0, a1, k);
  } else {
    k = (t - 0.35) / 0.65;
    c = lerpRgb(c1, c2, k);
    a = lerp(a1, a2, k);
  }
  return [
    c[0] * a + dst[0] * (1 - a),
    c[1] * a + dst[1] * (1 - a),
    c[2] * a + dst[2] * (1 - a),
  ];
}

/**
 * Composite the 2D sun halo + inner glow (source-over yellows) onto a backdrop.
 * Matches `drawSun` colour stops. Additive blending on a bright sky blows this
 * out to white — the GPU sky must mix these yellows the same way.
 *
 * @param {number[]} backdropRgb
 * @param {number} dist distance from sun centre
 * @param {number} radius disc radius
 * @param {number} [alpha]
 * @returns {number[]}
 */
export function sampleSunGlowOver(backdropRgb, dist, radius, alpha) {
  var dst = backdropRgb && backdropRgb.length >= 3
    ? [backdropRgb[0], backdropRgb[1], backdropRgb[2]]
    : [0, 0, 0];
  var r = Math.max(Number(radius) || 0, 1);
  var a = alpha == null ? 1 : Number(alpha) || 0;
  var d = Number(dist);
  if (!isFinite(d) || a <= 0) return dst;
  var haloT = Math.max(0, Math.min(1, (d - r * 0.35) / Math.max(r * 4.45, 1)));
  dst = overlayRadialRgb(
    dst, haloT,
    [255, 213, 110], 0.42 * a,
    [255, 200, 90], 0.18 * a,
    [255, 180, 60], 0,
  );
  var glowT = Math.max(0, Math.min(1, (d - r * 0.2) / Math.max(r * 3.0, 1)));
  dst = overlayRadialRgb(
    dst, glowT,
    [255, 230, 150], 0.55 * a,
    [255, 200, 90], 0.22 * a,
    [255, 180, 60], 0,
  );
  return dst;
}

/** Shadow-gradient stop where the moon terminator begins to fade out. Was 0.86 (~14% feather); 0.922 is ~80% narrower, still not a hard edge. */
export var MOON_SHADE_FADE_START = 0.922;

/**
 * Alpha of the offset shadow disc at `distFromShadeCenter` (same stops as `drawMoon`).
 * @param {number} distFromShadeCenter
 * @param {number} radius moon disc radius
 */
export function moonShadeAlpha(distFromShadeCenter, radius) {
  var r = Math.max(Number(radius) || 0, 1);
  var sr = r * 1.08;
  var t = (Number(distFromShadeCenter) - sr * 0.2) / Math.max(sr * 0.8, 1);
  t = Math.max(0, Math.min(1, t));
  var fadeStart = MOON_SHADE_FADE_START;
  if (t < fadeStart) return lerp(0.94, 0.92, t / fadeStart);
  return lerp(0.92, 0, (t - fadeStart) / (1 - fadeStart));
}

/**
 * Sun disc opacity across the day for the legacy normalized-phase path. Hidden
 * deep at night, brightest mid-morning, warm/faint near the horizon crossings.
 */
export function sunAlpha(timeOfDay) {
  var t = normalizeTimeOfDay(timeOfDay);
  if (t > 0.62 && t < 0.88) return 0;
  if (t <= 0.12) return lerp(0.35, 0.95, t / 0.12);
  if (t < 0.25) return lerp(0.95, 1, (t - 0.12) / 0.13);
  if (t <= 0.38) return 1;
  if (t < 0.5) return lerp(1, 0.55, (t - 0.38) / 0.12);
  if (t <= 0.62) return lerp(0.55, 0, (t - 0.5) / 0.12);
  if (t >= 0.88) return lerp(0, 0.4, (t - 0.88) / 0.12);
  return 0;
}

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} x
 * @param {number} y
 * @param {number} radius
 * @param {number} timeOfDay
 */
export function drawSun(ctx, x, y, radius, timeOfDay) {
  if (!ctx || !isFinite(x) || !isFinite(y)) return;
  var r = Math.max(0, Number(radius) || 0);
  if (r <= 0) return;

  var alpha = sunAlpha(timeOfDay);
  if (alpha <= 0.01) return;

  ctx.save();

  var outerHalo = ctx.createRadialGradient(x, y, r * 0.35, x, y, r * 4.8);
  outerHalo.addColorStop(0, "rgba(255, 213, 110, " + (0.42 * alpha).toFixed(3) + ")");
  outerHalo.addColorStop(0.35, "rgba(255, 200, 90, " + (0.18 * alpha).toFixed(3) + ")");
  outerHalo.addColorStop(1, "rgba(255, 180, 60, 0)");
  ctx.fillStyle = outerHalo;
  ctx.beginPath();
  ctx.arc(x, y, r * 4.8, 0, Math.PI * 2);
  ctx.fill();

  var glow = ctx.createRadialGradient(x, y, r * 0.2, x, y, r * 3.2);
  glow.addColorStop(0, "rgba(255, 230, 150, " + (0.55 * alpha).toFixed(3) + ")");
  glow.addColorStop(0.35, "rgba(255, 200, 90, " + (0.22 * alpha).toFixed(3) + ")");
  glow.addColorStop(1, "rgba(255, 180, 60, 0)");
  ctx.fillStyle = glow;
  ctx.beginPath();
  ctx.arc(x, y, r * 3.2, 0, Math.PI * 2);
  ctx.fill();

  var disc = ctx.createRadialGradient(x - r * 0.25, y - r * 0.25, r * 0.1, x, y, r);
  disc.addColorStop(0, "rgba(255, 246, 208, " + alpha.toFixed(3) + ")");
  disc.addColorStop(0.55, "rgba(255, 224, 138, " + alpha.toFixed(3) + ")");
  disc.addColorStop(0.92, "rgba(255, 201, 60, " + (0.95 * alpha).toFixed(3) + ")");
  // Feather the rim to clear so the disc edge melts into the warm haze instead of
  // cutting a hard circle.
  disc.addColorStop(1, "rgba(255, 201, 60, 0)");
  // A soft blurred bloom hugging the edge — atmospheric haze around the sun.
  ctx.shadowColor = "rgba(255, 220, 130, " + (0.85 * alpha).toFixed(3) + ")";
  ctx.shadowBlur = r * 0.55;
  ctx.fillStyle = disc;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.shadowBlur = 0;

  ctx.restore();
}

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} x
 * @param {number} y
 * @param {number} radius
 * @param {{ skyColor?: number[] }} [opts]
 */
export function drawMoon(ctx, x, y, radius, opts) {
  if (!ctx || !isFinite(x) || !isFinite(y)) return;
  var r = Math.max(0, Number(radius) || 0);
  if (r <= 0) return;
  opts = opts || {};

  ctx.save();

  // Soft outer glow / halo — kept faint so the close-in glow doesn't bloom too
  // brightly around the disc.
  var glow = ctx.createRadialGradient(x, y, r * 0.15, x, y, r * 2.6);
  glow.addColorStop(0, "rgba(230, 235, 255, 0.17)");
  glow.addColorStop(0.4, "rgba(200, 210, 245, 0.07)");
  glow.addColorStop(1, "rgba(180, 190, 230, 0)");
  ctx.fillStyle = glow;
  ctx.beginPath();
  ctx.arc(x, y, r * 2.6, 0, Math.PI * 2);
  ctx.fill();

  // Lit disc, lit from the upper-left, with gentle limb darkening.
  var lx = x - r * 0.22;
  var ly = y - r * 0.22;
  var disc = ctx.createRadialGradient(lx, ly, r * 0.05, lx, ly, r * 1.3);
  disc.addColorStop(0, "rgb(252, 253, 255)");
  disc.addColorStop(0.55, "rgb(226, 234, 245)");
  disc.addColorStop(1, "rgb(188, 203, 224)");
  // Soft blurred bloom around the rim — cool atmospheric haze so the moon doesn't
  // read as a hard-cut circle.
  ctx.shadowColor = "rgba(214, 226, 250, 0.32)";
  ctx.shadowBlur = r * 0.4;
  ctx.fillStyle = disc;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.shadowBlur = 0;

  // Everything below is clipped to the disc so nothing spills past the moon's
  // edge (the old crescent carve drew a circle that stuck out).
  ctx.save();
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.clip();

  // Maria / craters — faint darker patches that give the surface some texture.
  // [dx, dy, radius, alpha] in fractions of r, mostly on the lit (left) side.
  var craters = [
    [-0.34, -0.18, 0.26, 0.10],
    [-0.05, 0.30, 0.20, 0.10],
    [-0.48, 0.18, 0.14, 0.09],
    [0.06, -0.34, 0.16, 0.08],
    [-0.18, 0.02, 0.22, 0.07],
  ];
  for (var i = 0; i < craters.length; i++) {
    var c = craters[i];
    var cx = x + c[0] * r;
    var cy = y + c[1] * r;
    var cr = c[2] * r;
    var cg = ctx.createRadialGradient(cx, cy, 0, cx, cy, cr);
    cg.addColorStop(0, "rgba(120, 135, 165, " + c[3] + ")");
    cg.addColorStop(0.7, "rgba(120, 135, 165, " + (c[3] * 0.6).toFixed(3) + ")");
    cg.addColorStop(1, "rgba(120, 135, 165, 0)");
    ctx.fillStyle = cg;
    ctx.beginPath();
    ctx.arc(cx, cy, cr, 0, Math.PI * 2);
    ctx.fill();
  }

  // The sun's shadow → a lunar phase. An offset disc, clipped to the moon and
  // filled close to the night-sky colour so the dark side almost blends into the
  // background (like a real crescent: the lit scythe pops, the full disc is only
  // faintly visible). Nudged a touch toward the moon's glow so it doesn't read as
  // a hard dark hole, with a slightly feathered terminator (not a knife edge).
  // Dark side reflects the sky directly behind the moon (passed by the caller),
  // lifted only a hair so it reads as sky and melts into the background instead
  // of a distinct dark hole — a real full disc whose unlit part is barely there.
  var sky = opts.skyColor || [11, 30, 77];
  var shadowBase = lerpRgb(sky, [70, 92, 150], 0.1);
  var sx = x + r * 0.5;
  var sy = y - r * 0.04;
  var sr = r * 1.08;
  var shade = ctx.createRadialGradient(sx, sy, sr * 0.2, sx, sy, sr);
  shade.addColorStop(0, rgb(shadowBase, 0.94));
  shade.addColorStop(MOON_SHADE_FADE_START, rgb(shadowBase, 0.92));
  shade.addColorStop(1, rgb(shadowBase, 0));
  ctx.fillStyle = shade;
  ctx.beginPath();
  ctx.arc(sx, sy, sr, 0, Math.PI * 2);
  ctx.fill();

  ctx.restore(); // disc clip
  ctx.restore();
}
